/* eslint-disable @typescript-eslint/naming-convention */
import { Outlet, useLoaderData } from "@remix-run/react";
import { withSentry } from "@sentry/remix";

import { AviaryProvider } from "@aviary/utils";
import { Document } from "@remix/components/Document/Document";
import { ErrorBoundary } from "@remix/components/ErrorBoundary/ErrorBoundary";
import {
  ITERABLE_PRACTITIONER_WEB_API_KEY,
  RUDDERSTACK_DATA_PLANE_URL,
  RUDDERSTACK_JS_API_WRITE_KEY,
  GOOGLE_MAPS_API_PUB_KEY,
  SENTRY_DSN,
} from "@remix/config.server";
import { useSSRContext } from "@remix/context/SSRContext";
import { BannersProvider } from "@shared/components/Banners/BannersProvider";
import { LazyExperimentTester } from "@shared/components/ExperimentTester/LazyExperimentTester/LazyExperimentTester";
import { FlippersProvider } from "@shared/context/FlippersContext";
import { GlobalContextProvider as SharedGlobalContextProvider } from "@shared/context/GlobalConfig";
import { useInitialProps } from "@shared/context/InitialPropsProvider";
import { CAGlobals } from "@shared/data/globals/CA";
import { USGlobals } from "@shared/data/globals/US";
import { useCheckIsEmerson } from "@shared/hooks/useCheckIsEmerson/useCheckIsEmerson";
import { useHydrated } from "@shared/hooks/useHydrated/useHydrated";
import { useServer } from "@shared/hooks/useServer/useServer";
import { useLocation } from "@shared/react-router-dom/react-router-dom";
import { openCatalogRoutes } from "@unauthenticated/catalog/data/openCatalogRoutes";

import { getRole } from "./helpers/getRole/getRole";

type LoaderReturn = {
  ENV: AppEnv;
};

const loader = (): LoaderReturn => {
  // ⚠️ Anything returned here will be exposed on the client
  // Don't pass anything that is a secret!

  return {
    ENV: {
      CI_COMMIT_REF_NAME: process.env.CI_COMMIT_REF_NAME,
      CI_COMMIT_SHA: process.env.CI_COMMIT_SHA,
      E2E_ENV: process.env.E2E_ENV,
      ENABLE_SENTRY_TUNNEL: false,
      FORCE_ENABLE_SENTRY: process.env.FORCE_ENABLE_SENTRY === "true",
      GOOGLE_MAPS_API_PUB_KEY,
      IS_REMIX: true,
      NODE_ENV: process.env.NODE_ENV,
      REACT_AXE_ENABLED: process.env.REACT_AXE_ENABLED === "true",
      SENTRY_DSN,
      RUDDERSTACK_JS_API_WRITE_KEY,
      RUDDERSTACK_DATA_PLANE_URL,
      ITERABLE_PRACTITIONER_WEB_API_KEY,
      TARGET_ENV: process.env.TARGET_ENV,
    },
  };
};

const App = () => {
  const initialProps = useInitialProps();
  const { initialApolloState } = useSSRContext();
  const { ENV } = useLoaderData<typeof loader>();
  const { deviceType } = useServer();
  const isEmerson = useCheckIsEmerson();
  const hydrated = useHydrated();
  const location = useLocation();

  const { flippers, e2e, fullscriptCountry, isAdminImpersonating, roleId, userId, roleType } =
    initialProps;

  const isDS3 = location.pathname.startsWith(openCatalogRoutes.catalog);
  const country = fullscriptCountry === "US" ? USGlobals.country : CAGlobals.country;
  const role = getRole({ userId, roleId, roleType });

  return (
    <FlippersProvider flippers={flippers}>
      <SharedGlobalContextProvider
        isEmerson={isEmerson}
        country={country}
        role={role}
        e2e={e2e}
        adminImpersonating={isAdminImpersonating}
        rudderstackJsApiWriteKey={ENV.RUDDERSTACK_JS_API_WRITE_KEY}
        rudderstackDataPlaneUrl={ENV.RUDDERSTACK_DATA_PLANE_URL}
        iterableApiKey={ENV.ITERABLE_PRACTITIONER_WEB_API_KEY}
      >
        <BannersProvider>
          <Document
            {...{
              initialApolloState,
              initialProps,
              deviceType,
              ENV,
            }}
          >
            <AviaryProvider isDS3={isDS3} isEmerson={isEmerson} hydrated={hydrated}>
              <Outlet />
              <LazyExperimentTester />
            </AviaryProvider>
          </Document>
        </BannersProvider>
      </SharedGlobalContextProvider>
    </FlippersProvider>
  );
};

const AppWithSentry = withSentry(App);

export { AppWithSentry as default, ErrorBoundary, loader };
